import {
  type DefaultError,
  type QueryKey,
  type UseInfiniteQueryOptions,
  type UseQueryOptions,
  type WithRequired,
  hashKey,
  keepPreviousData,
  skipToken,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import * as React from 'react';

function useDebounce<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  createQuery: (
    searchTerm: string,
  ) => WithRequired<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    'queryKey'
  >,
  searchTerm: string,
  prefetch: true,
): UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>;
function useDebounce<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = number,
>(
  createQuery: (
    searchTerm: string,
  ) => WithRequired<
    UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>,
    'queryKey'
  >,
  searchTerm: string,
  prefetch: false,
): UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>;
function useDebounce<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = number,
>(
  createQuery: (
    searchTerm: string,
  ) => WithRequired<
    | UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>
    | UseInfiniteQueryOptions<
        TQueryFnData,
        TError,
        TData,
        TQueryKey,
        TPageParam
      >,
    'queryKey'
  >,
  searchTerm: string,
  prefetch: boolean,
) {
  const queryClient = useQueryClient();

  const [debouncedSearchTerm, setDebouncedSearchTerm] =
    React.useState(searchTerm);

  const freshQuery = React.useMemo(
    () => createQuery(searchTerm),
    [createQuery, searchTerm],
  );

  React.useEffect(() => {
    const queryData = queryClient.getQueryState(freshQuery.queryKey);

    // if we already have the query in our cache or the query is disabled, we don't need to debounce
    if (
      queryData ||
      freshQuery.enabled === false ||
      freshQuery.queryFn === skipToken
    ) {
      setDebouncedSearchTerm(searchTerm);

      return;
    }

    const timeout = setTimeout(() => {
      setDebouncedSearchTerm((currentQuery) => {
        // this only happens on first render
        if (currentQuery === searchTerm) {
          return currentQuery;
        }

        if (prefetch) {
          void queryClient.prefetchQuery(freshQuery as UseQueryOptions);
        }

        return searchTerm;
      });
    }, 200);

    return () => {
      clearTimeout(timeout);
    };
  }, [freshQuery, prefetch, queryClient, searchTerm]);

  return debouncedSearchTerm === searchTerm
    ? freshQuery
    : createQuery(debouncedSearchTerm);
}

export function useDebouncedQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  createQuery: (
    searchTerm: string,
  ) => WithRequired<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    'queryKey'
  >,
  searchTerm: string,
) {
  const query = useDebounce(createQuery, searchTerm, true);

  const queryKeyHash = hashKey(query.queryKey);

  const result = useQuery({
    placeholderData:
      query.enabled === false || query.queryFn === skipToken
        ? undefined
        : keepPreviousData,
    staleTime: 10_000,
    ...query,
  });

  return { query: result, queryKeyHash };
}

export function useDebouncedInfiniteQuery<
  TQueryFnData,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>(
  createQuery: (
    searchTerm: string,
  ) => WithRequired<
    UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>,
    'queryKey'
  >,
  searchTerm: string,
) {
  const query = useDebounce(createQuery, searchTerm, false);

  const queryKeyHash = hashKey(query.queryKey);

  const result = useInfiniteQuery({
    placeholderData:
      query.enabled === false || query.queryFn === skipToken
        ? undefined
        : keepPreviousData,
    staleTime: 10_000,
    // if `debounced_search_term === search_term`, this has the same result. If
    // `debounced_search_term !== search_term`, we're fetching the cached data
    // until debounce is done.
    ...query,
  });

  return { query: result, queryKeyHash };
}
